import { NO, UIColor, YES } from "uicore-ts"
import { RHOffererSubmissionOutcome } from "../../../SocketClientFunctions"
import { RHColor } from "../RHColor"
import { RHTables } from "./RHDesignTokens"
import { RHProcurementStatusSource, RHProcurementWinnerStatus } from "./RHProcurementWinnerStatus"


export interface RHProcurerProcurementStatusPresentation {
    text: string
    color: UIColor
    linksToOffers: boolean
}


/**
 * Legacy status-filter categories used outside the server-resolved lifecycle lists.
 * Each procurement belongs to only one lifecycle category.
 */
export type RHProcurementLifecycleCategory =
    | "avaldatudKaimas"
    | "hindamisel"
    | "lopetatud"
    | "tuhistatud"
    | "mustand"


/**
 * The procurement's actual, mutually-exclusive position in its lifecycle.
 * Production has no "evaluation" status and no per-part cancellation, so
 * "evaluating" and the "closed*" split are computed from status + deadline +
 * winnerKind rather than read off a single field. This is the ONE place that
 * computation happens — every status text and every filter-chip bucket is a
 * projection of this value, so they can never disagree with each other.
 */
export type RHProcurementLifecycleStage =
    | "draft"
    | "cancelled"
    | "open"                   // published, deadline not yet passed
    | "evaluating"              // published, deadline passed, not yet finalized, no full winner yet
    | "closedWithoutContract"   // explicitly ended without a contract
    | "closedNoWinner"          // finalized without any winner
    | "closedFullWinner"        // every part has a winner (finalized or not — see lifecycleStageForProcurement)
    | "closedPartialWinner"     // finalized with winners for only some parts


const offererSubmissionOutcomeTextByOutcome: Record<RHOffererSubmissionOutcome, string> = {
    evaluating: "Hindamisel",
    winner: "Märgitud edukaks",
    partialWinner: "Märgitud hankeosas edukaks",
    multiplePartsWinner: "Märgitud hankeosades edukaks",
    notWinner: "Sinu pakkumust ei tunnistatud edukaks",
    noWinner: "Ühtegi pakkumust ei tunnistatud edukaks",
    singlePartEvaluationFinalized: "Pakkumust osale ei tunnistatud edukaks",
    multiplePartsEvaluationFinalized: "Pakkumusi osadele ei tunnistatud edukateks",
    partClosedWithoutContract: "Hankeosa lõpetatud lepinguta",
    partsClosedWithoutContract: "Hankeosad lõpetatud lepinguta",
    partContractSigned: "Leping hankeosale on sõlmitud",
    partContractsSigned: "Lepingud hankeosadele on sõlmitud",
    partContractCompleted: "Leping hankeosale on lõppenud",
    partContractsCompleted: "Lepingud hankeosadele on lõppenud",
    contractSigned: "Leping on sõlmitud",
    contractNotConcluded: "Lepingut ei sõlmitud"
}


/**
 * SINGLE derivation of the procurer-facing procurement status text
 * (docs/statuses_and_order_human_table.md). Used by BOTH the "Töös hanked"
 * table and the procurer landing-page "Sinu hanked" table, so the same
 * procurement can never show two different statuses in two places.
 */
export class RHProcurementStatusPresentation {

    static textForOffererSubmissionOutcome(outcome: RHOffererSubmissionOutcome): string {
        return offererSubmissionOutcomeTextByOutcome[outcome]
    }

    /**
     * A full winner counts as "closed" the moment every part has one, even before
     * FinalizeEvaluation has formally run — a procurer who has just assigned the
     * last part's winner sees the procurement as done, whether or not the
     * finalization click has happened yet. A partial winner does NOT count as
     * closed until finalization, since some parts are still under evaluation.
     */
    static lifecycleStageForProcurement(procurement: RHProcurementStatusSource): RHProcurementLifecycleStage {
        if (procurement.status === "draft") {
            return "draft"
        }
        if (procurement.status === "draft-cancelled") {
            return "cancelled"
        }
        if (procurement.status === "closed-no-winner") {
            return "closedNoWinner"
        }
        if (procurement.status === "closed-without-contract") {
            return "closedWithoutContract"
        }
        if (procurement.status === "evaluation-finalized") {
            // FinalizeEvaluation only sets this status when a winner exists, so
            // winnerKind is never "none" here.
            if (RHProcurementWinnerStatus.winnerKind(procurement) === "full") {
                return "closedFullWinner"
            }
            return "closedPartialWinner"
        }

        const winnerKind = RHProcurementWinnerStatus.winnerKind(procurement)
        if (winnerKind === "full") {
            return "closedFullWinner"
        }

        let hasDeadlinePassed = NO
        if (procurement.deadlineDate) {
            hasDeadlinePassed = new Date(procurement.deadlineDate).getTime() < Date.now()
        }
        if (hasDeadlinePassed) {
            return "evaluating"
        }
        return "open"
    }


    /**
     * First match wins:
     *   1. draft                                 -> "Ettevalmistamisel"          (muted, no link)
     *   2. draft-cancelled                       -> "Tühistatud"                (black, no link)
     *   3. deadline not passed (timeLeft >= 0)   -> live countdown               (black, no link)
     *   4. deadline passed, no visible offers    -> "Pakkumusi ei tehtud"        (black, no link)
     *   5. evaluation closed without any winner  -> "Ühtegi pakkumust ei tunnistatud edukaks" (muted, links)
     *   6. full winner chosen                    -> "Oled märkinud pakkumuse edukaks" (success, links)
     *   7. only some parts have winners          -> "Oled märkinud pakkumuse hankeosas edukaks" (success, links)
     *   8. otherwise                             -> "Vaata pakkumusi"            (action blue, links)
     *
     * Colour scheme (conservative, per Rene 2026-06-23): the status column kept only
     * three accent colours — action blue ("Vaata pakkumusi"), success green (a winner
     * was chosen / contract signed), and muted grey (closed-out states + draft). Every
     * other state is plain black, so the column no longer reads as a wall of red/cyan.
     *
     * Quirk kept from the mock-up: branch 3 uses ">= 0" while branch 4 uses
     * "deadline passed", so exactly at the deadline moment the countdown wins.
     * The mock-up's other quirk (cancelled + full winner showing the winner text)
     * was a contradiction and is intentionally NOT reproduced — cancellation is
     * an absolute final state (event 22 in the status table).
     */
    static presentationForProcurerProcurementWithOfferCount(
        procurement: RHProcurementStatusSource,
        offerCount: number
    ): RHProcurerProcurementStatusPresentation {
        const stage = RHProcurementStatusPresentation.lifecycleStageForProcurement(procurement)

        if (stage === "draft") {
            return { text: "Ettevalmistamisel", color: RHColor.textMuted, linksToOffers: NO }
        }

        if (stage === "cancelled") {
            return { text: "Tühistatud", color: RHColor.textBlack, linksToOffers: NO }
        }

        if (stage === "closedWithoutContract") {
            return { text: "Lõpetatud lepinguta", color: RHColor.textMuted, linksToOffers: YES }
        }

        const timeLeft = RHProcurementStatusPresentation._timeLeftUntilProcurementDeadline(procurement)
        if (timeLeft !== undefined && timeLeft >= 0) {
            return {
                text: RHProcurementStatusPresentation._countdownTextForTimeLeft(timeLeft),
                color: RHColor.textBlack,
                linksToOffers: NO
            }
        }

        if (offerCount === 0) {
            return { text: "Pakkumusi ei tehtud", color: RHColor.textBlack, linksToOffers: NO }
        }

        // FinalizeEvaluation sets "closed-no-winner" when the procurer finalized
        // without choosing any winner — without this branch the row would fall
        // through to "Vaata pakkumusi" and look like evaluation is still open.
        if (stage === "closedNoWinner") {
            return { text: "Ühtegi pakkumust ei tunnistatud edukaks", color: RHColor.textMuted, linksToOffers: YES }
        }

        const winnerKind = RHProcurementWinnerStatus.winnerKind(procurement)

        if (winnerKind === "full") {
            return { text: "Oled märkinud pakkumuse edukaks", color: RHColor.success, linksToOffers: YES }
        }

        if (winnerKind === "partial") {
            return { text: "Oled märkinud pakkumuse hankeosas edukaks", color: RHColor.success, linksToOffers: YES }
        }

        return {
            text: "Vaata pakkumusi",
            color: RHTables.rowActionTextColor,
            linksToOffers: YES
        }
    }


    /** Whether a procurement belongs to one mutually exclusive lifecycle category. */
    static isProcurementInLifecycleCategory(
        procurement: RHProcurementStatusSource,
        category: RHProcurementLifecycleCategory
    ): boolean {
        const stage = RHProcurementStatusPresentation.lifecycleStageForProcurement(procurement)

        if (category === "mustand") {
            return stage === "draft"
        }
        if (category === "tuhistatud") {
            return stage === "cancelled"
        }
        if (category === "avaldatudKaimas") {
            return stage === "open"
        }
        if (category === "hindamisel") {
            return stage === "evaluating"
        }
        // lopetatud
        return stage === "closedNoWinner" ||
            stage === "closedWithoutContract" ||
            stage === "closedFullWinner" ||
            stage === "closedPartialWinner"
    }


    /**
     * NEUTRAL, ownership-agnostic status text for the global procurement search,
     * where rows belong to many different companies — so the procurer-facing
     * "Oled märkinud pakkumuse edukaks" (owner language) and the offer-count-dependent texts
     * must NOT be used. Derived from the same lifecycle categories as the filters.
     */
    static neutralStatusPresentationForProcurement(
        procurement: RHProcurementStatusSource
    ): { text: string, color: UIColor } {
        const stage = RHProcurementStatusPresentation.lifecycleStageForProcurement(procurement)

        if (stage === "cancelled") {
            return { text: "Tühistatud", color: RHColor.textMuted }
        }
        if (stage === "open") {
            const timeLeft = RHProcurementStatusPresentation._timeLeftUntilProcurementDeadline(procurement)
            if (timeLeft !== undefined && timeLeft >= 0) {
                return { text: RHProcurementStatusPresentation._countdownTextForTimeLeft(timeLeft), color: RHColor.textBlack }
            }
            return { text: "Avaldatud (käimas)", color: RHColor.textBlack }
        }
        if (stage === "evaluating") {
            return { text: "Hindamisel", color: RHColor.textBlack }
        }
        // Drafts never reach the search/neutral views (they're private), so this
        // falls to the same generic text the original always used for them.
        if (stage === "draft") {
            return { text: "Avaldatud", color: RHColor.textBlack }
        }
        return { text: "Lõpetatud", color: RHColor.textMuted }
    }


    /**
     * Milliseconds remaining until the deadline moment, or undefined when no
     * deadline is set. The deadline is stored as an absolute instant, so a plain
     * getTime() comparison already reflects Europe/Tallinn local time correctly.
     */
    static _timeLeftUntilProcurementDeadline(procurement: RHProcurementStatusSource): number | undefined {
        if (!procurement.deadlineDate) {
            return undefined
        }
        return new Date(procurement.deadlineDate).getTime() - Date.now()
    }


    static _countdownTextForTimeLeft(timeLeftMilliseconds: number): string {
        const days = Math.floor(timeLeftMilliseconds / 86400000)
        const hours = Math.floor((timeLeftMilliseconds % 86400000) / 3600000)
        const minutes = Math.floor((timeLeftMilliseconds % 3600000) / 60000)
        let dayUnit = "päeva"
        if (days === 1) {
            dayUnit = "päev"
        }
        let hourUnit = "tundi"
        if (hours === 1) {
            hourUnit = "tund"
        }
        let minuteUnit = "minutit"
        if (minutes === 1) {
            minuteUnit = "minut"
        }
        return `Tähtajani ${days} ${dayUnit}, ${hours} ${hourUnit}, ${minutes} ${minuteUnit}`
    }

}
